import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import logger from '@gral/log';

import { useNavigate, useLocation, matchPath } from 'react-router';
import url from 'url';
import ListRoutes from '@gral/routes';
import { IRoute } from '@interfaces/App.interface';
import { TMode } from '@ts/ui-models';
import { useAppSelector } from '@redux/store';
import { TEntity } from '@ts/ui-models';
import { Theme, useMediaQuery } from '@mui/material';

interface IRouterContext {
  mode: TMode;
  tEntity: TEntity; // entity type
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  transferPropsRoute: {
    id?: string;
    path?: string;
    params?: any;
    location: ReturnType<typeof useLocation>;
    query?: any;
    isEmbededMobile?: string;
  };
  isMobile?: boolean;
  aclId?: string;
}

const RouterContext = createContext<IRouterContext | null>(null);
const log = logger('RouterProvider');
export const RouterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const listRoutes: IRoute[] = useMemo(() => ListRoutes, []);
  const parsedUrl = useMemo(() => url.parse(location.pathname + location.search, true), [location.pathname, location.search]);
  const historyRoutes = useAppSelector(state => state.router.historyRoutes);
  const historyRoutesRef = useRef(historyRoutes);
  const mode = useMemo(() => getMode(location.pathname), [location.pathname]);
  const tEntity = useMemo(() => getTEntity(location.pathname), [location.pathname]);
  const route = listRoutes.find(r => matchPath(r.matchPath, location.pathname));

  useEffect(() => {
    historyRoutesRef.current = historyRoutes;
  }, [historyRoutes]);

  useEffect(() => {
    const handlePopState = event => {
      navigate(historyRoutesRef.current[historyRoutesRef.current.length - 2]?.fullPath ?? '/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const transferPropsRoute = useMemo(() => {
    if (route) {
      log.info('Changed route', route);
      const params = matchPath(route.matchPath, location.pathname)?.params;
      return {
        id: route.id,
        path: location.pathname + location.search,
        params,
        location,
        query: parsedUrl.query,
        isEmbededMobile: parsedUrl.query?.isEmbededMobile as string,
      };
    }
    return { location };
  }, [location.key, parsedUrl.query]);

  return (
    <RouterContext.Provider value={{ mode, navigate, location, transferPropsRoute, tEntity, isMobile, aclId: route?.aclId }}>
      {children}
    </RouterContext.Provider>
  );
};

// Helper function to determine mode
const getMode = (path: string): TMode => {
  if (path.endsWith('view')) return 'view';
  else if (path.endsWith('edit')) return 'edit';
  else if (path.endsWith('new')) return 'new';
  return '';
};

const getTEntity = (path: string): TEntity => {
  const tEntityURL = path.split('/')[1] as TEntity;
  return tEntityURL;
};

// Hook to use RouterContext in child components
export const useRouterContext = (): IRouterContext => {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useRouterContext must be used within a RouterProvider');
  return context;
};
